import React, { useEffect, useState } from 'react';

import { Botao, Field, Modal, Textarea } from '../../../ui';

interface ModalDistratoProps {
  aberto: boolean;
  /** Descrição de quem/qual contrato será cancelado. */
  mensagem: string;
  processando?: boolean;
  onConfirmar: (motivo: string) => void;
  onCancelar: () => void;
}

export const MINIMO_MOTIVO = 5;

/**
 * Distrato de contrato (at-40, item 43; reescrito na at-69, frente D/D7).
 *
 * ⚠️ **Não usa o `ModalConfirmacao`**, e isso não mudou: aquele é para sim/não e o
 * `detalhe` dele é `string`. Aqui o **motivo é obrigatório** (decisão de negócio de
 * 03/09/2026) — cancelar contrato sem registrar o porquê apaga a razão da decisão
 * do histórico.
 *
 * ## O que mudou na at-69
 *
 *   1. **Virou o `Modal` do kit.** O overlay era o `.confirmacao-overlay` +
 *      `modalDistrato.css`, com listener de `Escape` próprio e `aria-modal="true"`
 *      **sem prender o foco**. Ganhou de graça: foco que entra e volta, Tab
 *      circulando dentro e o título como `<h2>`.
 *   2. **O campo virou `Field` + `Textarea`**, com o **mínimo anunciado desde o
 *      início** na dica — antes o aviso só aparecia depois de digitar, e quem
 *      olhava o botão travado não sabia o que faltava.
 *   3. **Rodapé à direita** (`alinharRodape`), a convenção de par de ações.
 *
 * ✅ **O contrato de props não mudou** (`aberto`, `mensagem`, `processando`,
 * `onConfirmar(motivo)`, `onCancelar`) e o mínimo de 5 continua sendo a regra —
 * é migração de envelope (D7).
 *
 * ⚠️ `ocupado={processando}` é o que trava Esc e clique fora durante o `PATCH`.
 * O modal antigo já fazia isso à mão; aqui é propriedade do componente base.
 */
const ModalDistrato: React.FC<ModalDistratoProps> = ({
  aberto,
  mensagem,
  processando,
  onConfirmar,
  onCancelar,
}) => {
  const [motivo, setMotivo] = useState('');

  // Limpa ao reabrir: motivo de um cancelamento não pode vazar para o seguinte.
  useEffect(() => {
    if (aberto) setMotivo('');
  }, [aberto]);

  const valido = motivo.trim().length >= MINIMO_MOTIVO;
  const erro = motivo.length > 0 && !valido ? `Descreva o motivo com pelo menos ${MINIMO_MOTIVO} caracteres.` : '';

  return (
    <Modal
      aberto={aberto}
      titulo="Cancelar este contrato?"
      descricao={mensagem}
      tamanho="sm"
      ocupado={processando}
      aoFechar={onCancelar}
      alinharRodape="direita"
      rodape={
        <>
          <Botao variante="secundario" disabled={processando} onClick={onCancelar}>
            Voltar
          </Botao>
          <Botao
            variante="perigo"
            carregando={processando}
            disabled={!valido}
            onClick={() => onConfirmar(motivo.trim())}
          >
            Cancelar contrato
          </Botao>
        </>
      }
    >
      <Field
        rotulo="Motivo do cancelamento"
        obrigatorio
        dica={`Fica registrado no contrato. Mínimo de ${MINIMO_MOTIVO} caracteres.`}
        erro={erro}
        campo="motivo"
      >
        <Textarea
          rows={4}
          value={motivo}
          disabled={processando}
          onChange={(evento) => setMotivo(evento.target.value)}
          placeholder="Ex.: associado solicitou o distrato por telefone."
        />
      </Field>
    </Modal>
  );
};

export default ModalDistrato;
